"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { DEFAULT_SYMBOL, fetchCandles, fetchOrderbook } from "@/lib/api";
import { intervalAtom } from "@/lib/chart-prefs";
import { useBuySellTokens } from "@/lib/useBuySellTokens";
import AnimatedPrice from "./AnimatedPrice";
import AppHeader from "./AppHeader";
import ChartToolbar from "./ChartToolbar";
import ErrorBoundary from "./ErrorBoundary";
import IntelStrip from "./IntelStrip";
import OrderTicket from "./OrderTicket";
import Orderbook from "./Orderbook";
import OrdersPanel from "./OrdersPanel";
import PriceChart from "./PriceChart";
import TradesTape from "./TradesTape";

const fmt = (v: number | string) =>
  Number(v).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Ticker = ({ symbol }: { symbol: string }) => {
  // Sourced from the order book's mid/spread rather than /last-price: it's one poll we
  // already make, and mid is the honest "current price" on a book this thin.
  const { data } = useQuery({
    queryKey: ["orderbook", symbol],
    queryFn: ({ signal }) => fetchOrderbook(symbol, 12, signal),
    refetchInterval: 4_000,
  });

  const stats = data?.stats;

  return (
    <div className="flex items-baseline gap-4">
      <div>
        <div className="text-xs text-fg-muted">{symbol.replace("-", " / ")}</div>
        <AnimatedPrice
          value={stats ? Number(stats.mid_price) : null}
          className="text-2xl text-fg"
        />
      </div>
      {stats && (
        <div className="tnum text-xs text-fg-muted">
          <div>
            bid <span className="text-buy">{fmt(stats.best_bid)}</span>
          </div>
          <div>
            ask <span className="text-sell">{fmt(stats.best_ask)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

type SidePanel = "book" | "trades" | "orders";

const MarketView = () => {
  const symbol = DEFAULT_SYMBOL;
  const interval = useAtomValue(intervalAtom);
  const [panel, setPanel] = useState<SidePanel>("book");

  // Keeps --buy/--sell in step with the user's candle colours across the whole UI.
  useBuySellTokens();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["candles", symbol, interval],
    queryFn: ({ signal }) => fetchCandles(symbol, interval, signal),
    refetchInterval: 15_000,
  });

  const candles = data?.candles ?? [];

  return (
    // Desktop: lock to the viewport (h-dvh) for a fixed trading-terminal layout with panels
    // that scroll internally. Mobile: min-h-dvh so the page flows and scrolls naturally, 
    // stacking a full chart + book + trades into a locked viewport just crushes each panel.
    <main className="mx-auto flex min-h-dvh w-full max-w-[1400px] flex-col gap-4 p-4 lg:h-dvh">
      <AppHeader>
        <Ticker symbol={symbol} />
      </AppHeader>

      {/* Ticker moves below the header on small screens, where the logo + connect button
          already fill the row. */}
      <div className="sm:hidden">
        <Ticker symbol={symbol} />
      </div>

      <ErrorBoundary fallbackLabel="Market intel unavailable.">
        <IntelStrip />
      </ErrorBoundary>

      {/* flex-1 + min-h-0 only bite on desktop, where the parent is height-locked and the
          chart must give up height rather than push the footer offscreen. On mobile the grid
          is plain document flow. */}
      <div className="grid grid-cols-1 gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-[1fr_300px]">
        <section className="flex h-[58vh] min-h-[320px] flex-col overflow-hidden rounded-lg border border-line bg-surface lg:h-auto lg:min-h-[420px]">
          <div className="border-b border-line p-3">
            <ChartToolbar />
          </div>

          <div className="relative min-h-0 flex-1">
            {isLoading && (
              <div className="absolute inset-0 grid place-items-center text-xs text-fg-muted">
                Loading chart…
              </div>
            )}
            {isError && (
              <div className="absolute inset-0 grid place-items-center px-6 text-center text-xs text-sell">
                Couldn&apos;t load candles: {(error as Error)?.message}
                <br />
                <span className="text-fg-muted">Is dubu-api running with the ingester enabled?</span>
              </div>
            )}
            {!isLoading && !isError && candles.length === 0 && (
              <div className="absolute inset-0 grid place-items-center px-6 text-center text-xs text-fg-muted">
                No trades in this window yet.
                <br />
                This market trades roughly 5x per hour. Try a longer interval.
              </div>
            )}
            <PriceChart candles={candles} className="h-full w-full" />
          </div>

          {/* Daya publishes no historical data, so our chart genuinely cannot predate the
              ingester. Stating it beats letting an empty chart imply a bug. */}
          {data?.history_starts_at && (
            <div className="border-t border-line px-3 py-1.5 text-[11px] text-fg-muted">
              Price history begins{" "}
              {new Date(data.history_starts_at).toLocaleString("en-NG", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </div>
          )}
        </section>

        <div className="flex flex-col gap-4 lg:min-h-0">
          <ErrorBoundary fallbackLabel="Trade panel failed to load.">
            <OrderTicket symbol={symbol} />
          </ErrorBoundary>

          {/* Mobile: a fixed, comfortable height so the book/trades show ~12 rows and scroll
              internally, with the page scrolling past. Desktop: fills the locked column. */}
          <aside className="flex h-[440px] flex-col overflow-hidden rounded-lg border border-line bg-surface lg:h-auto lg:min-h-0 lg:flex-1">
            <div className="flex border-b border-line" role="tablist">
              {(["book", "trades", "orders"] as SidePanel[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={panel === p}
                  onClick={() => setPanel(p)}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    panel === p ? "bg-raised text-fg" : "text-fg-muted hover:text-fg"
                  }`}
                >
                  {p === "book" ? "Book" : p === "trades" ? "Trades" : "Orders"}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1">
              {panel === "book" ? <Orderbook symbol={symbol} /> : panel === "trades" ? <TradesTape symbol={symbol} /> : <OrdersPanel />}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default MarketView;
